/**
 * CalculatorFlowService - Servicio para la funcionalidad de calculadora
 * 
 * Este servicio maneja las operaciones específicas de la calculadora de materiales,
 * incluyendo la obtención de ladrillos, cálculos de techos y recomendaciones.
 */

import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env';

// Interface para las regiones
export interface RegionDto {
  regionId: string;
  name: string;
  imgUrl: string | null;
  sortOrder: number;
}

// Interface para información de tragaluces
export interface SkylightDto {
  length: number;
  width: number;
}

// Interface que mapea la respuesta real de /v1/calculator/bricks
export interface CalculatorBrickDto {
  calculatorBrickId: string;
  thickness: number;
  unit: string;
  imgUrl: string;
  sortOrder: number;
}

// Interface para información de vigas
export interface BeamDto {
  length: number;
  width: number;
  depth: number;
}

// Interface para losas sólidas
export interface SolidSlabDto {
  length: number;
  width: number;
}

// Interface para el cálculo de Losa y Viga
export interface LosaVigaCalculationRequestDto {
  slabLength: number;
  slabWidth: number;
  brickThickness: number;
  strength: number;
  regionId: string;
  isBeachZone: boolean;
  skylights: SkylightDto[];
  beams: BeamDto[];
  solidSlabs: SolidSlabDto[];
  hasStaircase: boolean;
  workTypeId: string;
  mixerType?: 'smallMixer' | 'mixer' | 'hopper';
}

// Interface para el cálculo de Cimentación
export interface CimentacionCalculationRequestDto {
  length: number;
  width: number;
  height: number;
  strength: number;
  regionId: string;
  isBeachZone: boolean;
  workTypeId: string;
  mixerType?: 'smallMixer' | 'mixer' | 'hopper';
}

// Interface para el cálculo de Vereda
export interface VeredaCalculationRequestDto {
  initialHeight: number;
  finalHeight: number;
  strength: number;
  regionId: string;
  isBeachZone: boolean;
  workTypeId: string;
  mixerType?: 'smallMixer' | 'mixer' | 'hopper';
  length: number;
  width: number;
}

// Union type para todos los tipos de request
export type CalculationRequestDto = LosaVigaCalculationRequestDto | CimentacionCalculationRequestDto | VeredaCalculationRequestDto;

// Interface para las recomendaciones (POST): extiende el request con el ID del cálculo previo
export type RecommendationsRequestDto = CalculationRequestDto & { sourceCalculationId?: string | null };

// Interface para los ladrillos disponibles
export interface BrickDto {
  brickId: string;
  name: string;
  dimensions: string;
  thickness: number;
  description?: string;
  imgUrl?: string;
  isActive: boolean;
}

// Sub-interfaces para la respuesta del cálculo de techo
export interface CalculationMaterialQuantity {
  quantity: number;
  unit: string;
}

export interface CalculationMaterialPerBatch {
  smallMixer: CalculationMaterialQuantity;
  mixer: CalculationMaterialQuantity;
  hopper: CalculationMaterialQuantity;
}

export interface CalculationMaterial {
  productId: string
  name: string;
  imgUrl: string;
  total: CalculationMaterialQuantity;
  perBatch: CalculationMaterialPerBatch;
}

export interface CalculationInputSummary {
  region: string;
  element: string;
  strength: number;
  length: number;
  lengthUnit: string;
  width: number;
  widthUnit: string;
  height: number;
  heightUnit: string;
  brickThickness: number;
  brickThicknessUnit: string;
  hasSkylight: boolean;
  hasBeams: boolean;
  hasSolidSlab: boolean;
  hasStaircase: boolean;
  initialHeight: number;
  finalHeight: number;
  averageHeight: number;
  averageHeightUnit: string;
}

// Interface para la respuesta del cálculo
export interface CalculationResponseDto {
  calculationId?: string;
  totalVolumeM3: number;
  waterPerBagLiters: number;
  inputSummary: CalculationInputSummary;
  preListSectionId: string;
  materials: CalculationMaterial[];
  recommendations: RecommendationProduct[];
}

// Interface para un producto de la pre-lista de recomendaciones
export interface RecommendationProduct {
  productId: string;
  productName: string;
  category: string;
  quantity: number;
  unit: string;
  basePrice: string;
  imgUrl: string;
  preListItemId: string;
  brand: string;
}

// Interface para la respuesta de recomendaciones (nueva estructura del API)
export interface RecommendationsResponseDto {
  totalVolumeM3: number;
  waterPerBagLiters: number;
  inputSummary: CalculationInputSummary;
  materials: CalculationMaterial[];
  workTypeId: string;
  preListId: string;
  preListSectionId: string;
  recommendations: RecommendationProduct[];
  sourceCalculationId?: string; // ID del cálculo original que generó las recomendaciones
}

// Interface para la respuesta de resistencias
export interface StrengthItemDto {
  value: number;
  unit: string;
}

export interface StrengthsResponseDto {
  cementType: string;
  strengths: StrengthItemDto[];
}

@Injectable({ providedIn: 'root' })
export class CalculatorFlowService {
  private http = inject(HttpClient);

  // ── Estado compartido entre vistas del flujo ──────────────────────────────
  private _currentCalculationRequest = signal<CalculationRequestDto | null>(null);
  private _currentCalculationResult = signal<CalculationResponseDto | null>(null);
  private _currentCalculationId = signal<string | null>(null);
  private _currentPreListId = signal<string | null>(null);

  readonly currentCalculationRequest = this._currentCalculationRequest.asReadonly();
  readonly currentCalculationResult = this._currentCalculationResult.asReadonly();
  readonly currentCalculationId = this._currentCalculationId.asReadonly();
  readonly currentPreListId = this._currentPreListId.asReadonly();

  setCurrentCalculationRequest(req: CalculationRequestDto | null): void {
    this._currentCalculationRequest.set(req);
  }

  setCurrentCalculationResult(result: CalculationResponseDto | null): void {
    this._currentCalculationResult.set(result);
  }

  setCurrentCalculationId(id: string | null): void {
    this._currentCalculationId.set(id);
  }

  setCurrentPreListId(id: string | null): void {
    this._currentPreListId.set(id);
  }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Obtiene la lista de regiones disponibles
   * @returns Observable<RegionDto[]>
   */
  getRegions(): Observable<RegionDto[]> {
    return this.http.get<RegionDto[]>(`${environment.apiUrl}/v1/calculator/regions`);
  }

  /**
   * Obtiene la lista de ladrillos disponibles
   * @returns Observable<CalculatorBrickDto[]>
   */
  getBricks(): Observable<CalculatorBrickDto[]> {
    return this.http.get<CalculatorBrickDto[]>(`${environment.apiUrl}/v1/calculator/bricks`);
  }

  /**
   * Obtiene las resistencias disponibles según el tipo de trabajo y zona
   * @param workTypeId - ID del tipo de trabajo
   * @param isBeachZone - Si es zona de playa
   * @returns Observable<StrengthsResponseDto>
   */
  getStrengths(workTypeId: string, isBeachZone: boolean): Observable<StrengthsResponseDto> {
    return this.http.get(`${environment.apiUrl}/v1/calculator/strengths?workTypeId=${workTypeId}&isBeachZone=${isBeachZone}`) as Observable<StrengthsResponseDto>;
  }

  /**
   * Realiza el cálculo de materiales para el trabajo seleccionado
   * @param request - Datos del proyecto para el cálculo
   * @returns Observable<CalculationResponseDto>
   */
  calculateWorkType(request: CalculationRequestDto): Observable<CalculationResponseDto> {
    return this.http.post<CalculationResponseDto>(
      `${environment.apiUrl}/v1/calculator/calculate`, 
      request
    );
  }

  /**
   * Descarga el PDF con los resultados del cálculo
   * @param request - Datos del proyecto para el cálculo
   * @returns Observable<Blob> con el archivo PDF
   */
  downloadCalculatePdf(request: CalculationRequestDto): Observable<Blob> {
    return this.http.post(
      `${environment.apiUrl}/v1/calculator/calculate/pdf`,
      request,
      { responseType: 'blob' }
    );
  }

  /**
   * Obtiene recomendaciones para el cálculo
   * @param request - Datos del proyecto para las recomendaciones
   * @returns Observable<RecommendationsResponseDto>
   */
  getCalculateRecommendations(request: RecommendationsRequestDto): Observable<RecommendationsResponseDto> {
    return this.http.post<RecommendationsResponseDto>(
      `${environment.apiUrl}/v1/calculator/calculate-with-products`, 
      request
    );
  }
}